import {
  Flex,
  Image,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderMarkProps,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/react";
import { FieldProps } from "@rjsf/utils";
import { useRef } from "react";
import { FormattedMessage } from "react-intl";

import { DISABLED_OPACITY } from "@nens/ranalib";

import CellSizeBig from "@/images/cell-size-big.png";
import CellSizeSmall from "@/images/cell-size-small.png";

import { DEFAULT_CELL_SIZE } from "../constants";
import { useAreaSize } from "../hooks/use-area-size";

import { FileSize } from "./process-run-form-cell-size-file-size";

const TRANSFORM_Y = "90%";

const MARKER_PROPS: Partial<SliderMarkProps> = {
  top: 0,
  bg: "brand.bg",
  whiteSpace: "nowrap",
  rounded: "base",
  px: 1,
  mt: -0.5,
  fontSize: "sm",
};

const getStepSize = (cellSize: number) => {
  if (cellSize <= 5) return 0.5;
  if (cellSize <= 10) return 1;
  if (cellSize <= 100) return 5;
  return 10;
};

const getClosestSteppedValue = (cellSize: number): number => {
  const stepSize = getStepSize(cellSize);
  return Math.floor(cellSize / stepSize) * stepSize;
};

export function ProcessRunFormCellSizePicker(props: FieldProps) {
  const { onChange, schema, formData, readonly, disabled } = props;
  const isReadOnly = readonly || disabled;

  const labelRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);

  const labelRight = labelRef.current?.getBoundingClientRect().right;
  const valueLeft = valueRef.current?.getBoundingClientRect().left;
  const labelsOverlaping = labelRight && valueLeft ? valueLeft - labelRight < 5 : false;

  const defaultValue = schema.default ? Number(schema.default) : DEFAULT_CELL_SIZE;
  const cellSize = formData ?? defaultValue;
  const minimum = schema.minimum ?? 0.01;
  const maximum = schema.maximum ?? 100;

  const { isLoading, knowsAreaSize } = useAreaSize(props);

  return (
    <Flex py={6} opacity={isReadOnly ? DISABLED_OPACITY : 1} alignItems="center">
      <Image src={CellSizeSmall} />

      <Slider
        mx={3}
        h="fit"
        min={minimum}
        max={maximum}
        onChange={(value) => onChange(getClosestSteppedValue(value))}
        defaultValue={getClosestSteppedValue(cellSize)}
        step={getStepSize(cellSize)}
        isReadOnly={isReadOnly}
      >
        <SliderMark
          {...MARKER_PROPS}
          ref={labelRef}
          pl={0}
          value={minimum}
          transform={`translate(0, -${TRANSFORM_Y})`}
          transition="all 300ms"
          opacity={labelsOverlaping ? 0 : 1}
        >
          <FormattedMessage id="process.cell_size" defaultMessage="Cell size" />:
        </SliderMark>

        <SliderMark
          {...MARKER_PROPS}
          pl={0}
          value={minimum}
          transform={`translate(0, ${TRANSFORM_Y})`}
          transition="all 300ms"
          opacity={labelsOverlaping ? 0 : 1}
        >
          <FormattedMessage id="process.file_size" defaultMessage="File size" />:
        </SliderMark>

        <SliderMark
          {...MARKER_PROPS}
          ref={valueRef}
          value={Math.min(maximum, cellSize)}
          transform={`translate(-50%, -${TRANSFORM_Y})`}
        >
          {cellSize} m
        </SliderMark>

        <SliderMark
          {...MARKER_PROPS}
          // If it doesnt know the area size yet display this slider in the middle
          value={isLoading || knowsAreaSize ? Math.min(maximum, cellSize) : (minimum + maximum) / 2}
          transform={`translate(-50%, ${TRANSFORM_Y})`}
        >
          <FileSize {...props} />
        </SliderMark>

        <SliderTrack>
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb transition="opacity 300ms" opacity={cellSize > maximum ? 0 : 1} />
      </Slider>
      <Image src={CellSizeBig} />
    </Flex>
  );
}
